'use client';

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { FiTarget } from "react-icons/fi";
import { RxCheckCircled } from "react-icons/rx";
import { FcGoogle } from "react-icons/fc";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import logoIcon from "../../Assets/icons/logoNew.svg";
import {
  Typography,
  Link,
  Box,
  TextField,
  CssBaseline,
  Avatar,
  Container,
  Grid,
} from "@mui/material";
import { postLoginRequestData } from "../../Api/create";
import Notification from "../../Components/Notification";
import Controls from "../../Components/Controls";
import GoogleLogo from "./googleLogo.png";
import { hostConfig } from "../../Config";
import "./modern.css";

function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" align="center">
      {"Copyright © "}
      <Link
        color="inherit"
        href="http://www.pacificmedicalgroup.org/"
        target="_blank"
      >
        <span className="tooltip">
          <span className="tooltip-text">
            PACIFIC INPATIENT MEDICAL GROUP
          </span>
          PIMG,
        </span>
      </Link>{" "}
      all rights reserved. {new Date().getFullYear()}
    </Typography>
  );
}

const SignIn = () => {
  const { handleSubmit, register } = useForm();
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    hasNumber: false,
    hasCharacter: false,
  });

  if (JSON.parse(localStorage.getItem("loggedUser") || "null")) {
    window.location.href = "/dashboard";
  }

  const currentUserRole = (userRole) => {
    if (userRole.includes("admin")) {
      localStorage.setItem("currentUser", "admin");
    } else if (userRole.includes("approver")) {
      localStorage.setItem("currentUser", "approver");
    } else if (userRole.includes("sponsor")) {
      localStorage.setItem("currentUser", "sponsor");
    } else {
      localStorage.setItem("currentUser", "resource");
    }
  };

  const validatePassword = (password) => {
    setPasswordRules({
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasCharacter: /[A-Za-z]/.test(password)
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const responseHandler = (res) => {
    if (!res) return false;
    if (res.code) {
      setNotify({
        isOpen: true,
        message: res.message || "Bad Request",
        type: "error",
      });
      return false;
    }
    if (res.error) {
      setNotify({
        isOpen: true,
        message: res.error,
        type: "error",
      });
      return false;
    }
    return res;
  };

  const onSubmit = handleSubmit((data) => {
    // Use the password from state if available, otherwise use form data
    const passwordValue = password || data.password;
    const submitData = { ...data, password: passwordValue };
    
    if (submitData.email && submitData.password) {
      postLoginRequestData(submitData).then((res) => {
        if (responseHandler(res)) {
          let loggedUser = res.user;
          let userRole = res.user.role;
          let { access, refresh } = res.tokens;

          currentUserRole(userRole);

          localStorage.setItem("accessExpiry", access.expires);
          localStorage.setItem("refreshExpiry", refresh.expires);
          localStorage.setItem("accessToken", access.token);
          localStorage.setItem("refreshToken", refresh.token);
          localStorage.setItem("loggedUser", JSON.stringify(loggedUser));

          if (JSON.parse(localStorage.getItem("loggedUser") || "null")) {
            setNotify({
              isOpen: true,
              message: "Logged in successfully",
              type: "success",
            });
            window.location.href = "/dashboard";
          } else {
            setNotify({
              isOpen: true,
              message: "Invalid details",
              type: "error",
            });
          }
        }
      });
    } else {
      setNotify({
        isOpen: true,
        message: "Invalid details",
        type: "error",
      });
    }
  });

  return (
    <div className="modern-login-container">
      <div className="modern-login-card">
        <form onSubmit={onSubmit} className="modern-login-form">
          {/* Logo/Avatar Section */}
          <div className="modern-login-header">
            <img 
              src={logoIcon} 
              alt="Logo" 
              className="modern-logo"
              style={{
                width: '80%',
                height: '80%',
                margin: '0 auto 16px',
                display: 'block'
              }}
            />
            <h2 className="modern-title">Sign in to your account</h2>
          </div>

          {/* Email Field */}
          <div className="modern-form-group">
            <label className="modern-label">Email Address</label>
            <TextField
              {...register("email")}
              type="email"
              placeholder="Enter your email"
              variant="outlined"
              fullWidth
              autoComplete="email"
              autoFocus
              className="modern-textfield"
            />
          </div>

          {/* Password Field */}
          <div className="modern-form-group">
            <label className="modern-label">Password</label>
            <div className="modern-password-wrapper">
              <TextField
                {...register("password")}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                variant="outlined"
                fullWidth
                autoComplete="current-password"
                className="modern-textfield"
                value={password}
                onChange={handlePasswordChange}
              />
              <span
                className="modern-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                {showPassword ? <BsEye size={18} /> : <BsEyeSlash size={18} />}
              </span>
            </div>
          </div>

          {/* Password Rules */}
          <div className="modern-password-rules">
            <ul>
              <li className={passwordRules.hasNumber ? 'rule-satisfied' : 'rule-unsatisfied'}>
                <span className="rule-icon">
                  {passwordRules.hasNumber ? <RxCheckCircled /> : <FiTarget />}
                </span>
                Contains at least one number
              </li>
              <li className={passwordRules.hasCharacter ? 'rule-satisfied' : 'rule-unsatisfied'}>
                <span className="rule-icon">
                  {passwordRules.hasCharacter ? <RxCheckCircled /> : <FiTarget />}
                </span>
                Contains at least one letter
              </li>
              <li className={passwordRules.minLength ? 'rule-satisfied' : 'rule-unsatisfied'}>
                <span className="rule-icon">
                  {passwordRules.minLength ? <RxCheckCircled /> : <FiTarget />}
                </span>
                At least 8 characters
              </li>
            </ul>
          </div>

          {/* Sign In Button */}
          <Controls.ActionButton
            type="submit"
            variant="contained"
            fullWidth
            className="modern-signin-button"
          >
            Sign In
          </Controls.ActionButton>

          {/* OR Divider */}
          <p className="modern-divider">OR</p>

          {/* Google Sign In */}
          <a
            href={`${hostConfig.API_URL}auth/google`}
            className="modern-google-button"
          >
           <FcGoogle style={{ marginRight: '6px' }} /> 
            Sign in with Google
          </a>

          {/* Copyright */}
          <div className="modern-footer">
            <Copyright />
            <div className="modern-version">
              <center>Last Update: 23/Mar/2021</center>
              <center>Data Version: V26</center>
            </div>
          </div>
        </form>
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    </div>
  );
};

export default SignIn;